import { User } from '../models/core.models';
import {
  authCheck,
  listCompanyObjectsSuccess,
  login,
  loginFailure,
  loginSuccess,
  logout,
  remoteLogin,
  remoteLoginFail,
  remoteLoginSuccess,
  setRedirectUrl,
  switchUserCompanySuccess,
} from '../actions/auth.actions';
import { createReducer, on } from '@ngrx/store';

import { editUserSuccess } from 'src/app/admin/actions/admin-users.actions';
import { produce } from 'immer';

// Define the shape of the authentication state
export interface UserState {
  // --- auth lifecycle ---
  isAuthChecked: boolean; // Flag to check if authentication status has been checked
  isAuthLoading: boolean; // Flag to indicate if an authentication request is in progress
  isAuthenticated: boolean; // Flag to indicate if the user is authenticated
  isAuthResolved: boolean; // Flag to indicate if the authentication state status has been resolved
  isAuthError?: boolean; // Flag to indicate if there was an error during authentication
  authError?: string; // Store the error message if any during authentication

  // --- auth tokens ---
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresAt: number | null; // unix seconds
  refreshTokenExpiresAt: number | null; // unix seconds
  // --- user ---
  user: User; // Store the authenticated user's data
  redurectUrl?: string; // Optional redirect URL after successful authentication
  // --- companies / misc ---
  listCompanyObjects: boolean;
}

// Define the initial state for authentication
export const initialState: UserState = {
  // --- auth lifecycle ---
  isAuthChecked: false,
  isAuthLoading: false,
  isAuthenticated: false,
  isAuthError: false,
  isAuthResolved: false,

  // --- auth tokens ---
  accessToken: null,
  refreshToken: null,
  accessTokenExpiresAt: null, // unix seconds
  refreshTokenExpiresAt: null, // unix seconds

  user: null,
  listCompanyObjects: false,
};

// Create the authentication reducer
export const userReducer = createReducer(
  initialState,
  // Handle the login action
  on(login, remoteLogin, (state) => ({
    ...state,
    isAuthLoading: true, // Set loading to true when login starts
    isAuthenticated: false,
    isAuthError: false,
    authError: null,
    user: null,
  })),
  // Handle the login success action
  on(loginSuccess, remoteLoginSuccess, (state, { response }) => ({
    ...state,
    isAuthLoading: false, // Set loading to false when login succeeds
    isAuthenticated: true, // Set authenticated to true on success
    isAuthChecked: true,
    isAuthError: false,
    authError: null,
    user: response.user,
  })),
  // Handle the login failure action
  on(loginFailure, remoteLoginFail, (state, { error }) => ({
    ...state,
    isAuthChecked: true,
    isAuthLoading: false, // Set loading to false when login fails
    isAuthenticated: false,
    isAuthError: true, // Set error flag to true on failure
    authError: error,
    user: null,
  })),
  // Handle the authentication check action
  on(authCheck, (state, {}) => ({
    ...state,
    isAuthChecked: true, // Set checked flag to true when authentication status is verified
  })),

  on(switchUserCompanySuccess, (state, { user }) => {
    return {
      ...state,
      user: user,
    };
  }),

  on(setRedirectUrl, (state, { url }) => {
    return {
      ...state,
      redurectUrl: url,
    };
  }),

  on(
    editUserSuccess,
    produce((draft, { userData }) => {
      if (draft.user.id !== userData.id) return;

      Object.assign(draft.user, userData);
    }),
  ),

  on(
    listCompanyObjectsSuccess,
    produce((draft, { listCompanyObjects }) => {
      draft.user.list_company_objects = listCompanyObjects;
    }),
  ),

  on(logout, () => initialState),
);
